/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import { DOC_TYPES, labelFor, relativeTime, formatDate } from "@/lib/constants";

export default function PortalHistory() {
    const { activeClientId } = useAuth();
    const [subs, setSubs] = useState([]);

    async function load() {
        try {
            const params = activeClientId ? { client_id: activeClientId } : {};
            const { data } = await api.get("/documents/", { params });
            setSubs(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => { load(); /* eslint-disable-line */ }, [activeClientId]);

    async function downloadFile(submissionId, idx) {
        try {
            const { data } = await api.get(`/documents/${submissionId}/file/${idx}`);
            const a = document.createElement("a");
            a.href = `data:${data.mime};base64,${data.data_base64}`;
            a.download = data.name;
            a.click();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div>
            <div className="co-label">History</div>
            <h1 className="font-display text-3xl tracking-tight font-medium mt-1">What you&apos;ve sent</h1>
            <p className="text-sm text-[#5E636E] mt-2">Every document you&apos;ve uploaded, tagged by type and period.</p>

            <div className="mt-6">
                {subs.length === 0 ? (
                    <EmptyState icon={FileText} title="No documents yet" description="Head to Upload to send your first file." />
                ) : (
                    <ul className="space-y-2" data-testid="portal-history-list">
                        {subs.map((s) => (
                            <li key={s.id} className="co-card co-card-pad" data-testid={`portal-history-${s.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-medium text-sm">{labelFor(DOC_TYPES, s.document_type)}</div>
                                        <div className="text-xs text-[#5E636E] mt-0.5">
                                            {formatDate(s.period_start)} → {formatDate(s.period_end)} · {relativeTime(s.created_at)}
                                        </div>
                                        {s.review_notes && (
                                            <div className="mt-2 rounded-md bg-zinc-50 border border-[#E4E4E7] px-3 py-2 text-xs">
                                                <span className="font-medium">Accountant note: </span>{s.review_notes}
                                            </div>
                                        )}
                                        {s.files?.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {s.files.map((f, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => downloadFile(s.id, i)}
                                                        className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2 py-1 text-[11px] font-mono hover:bg-zinc-50"
                                                        data-testid={`portal-history-file-${s.id}-${i}`}
                                                    >
                                                        <Download className="h-3 w-3" /> {f.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <StatusBadge status={s.status} />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
